import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Key,
  Bell,
  LogOut,
  Loader2,
  AlertCircle,
  Save,
  Trash2,
  LogIn,
  LogOut as LogOutIcon,
  FileText,
  CheckCircle2,
  XCircle,
  Ban,
  User,
  Settings,
  Upload,
  Shield,
  Activity,
  ChevronDown,
  ChevronUp,
  MapPin,
  Clock,
  Monitor,
  Globe,
  RefreshCw,
  Filter,
  Search,
  Eye,
  Layers,
  ArrowRight,
  Info,
  Calendar,
  Edit3,
} from "lucide-react";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { useLocation } from "wouter";

// ─── Module colour palette ────────────────────────────────────────────────────

const MODULE_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  Dashboard:          { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
  Requests:           { bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500" },
  Approvals:          { bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500" },
  Facilities:         { bg: "bg-teal-100",   text: "text-teal-700",   dot: "bg-teal-500" },
  Security:           { bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500" },
  Administration:     { bg: "bg-slate-100",  text: "text-slate-700",  dot: "bg-slate-500" },
  Settings:           { bg: "bg-gray-100",   text: "text-gray-700",   dot: "bg-gray-500" },
  "Integration Hub":  { bg: "bg-cyan-100",   text: "text-cyan-700",   dot: "bg-cyan-500" },
  Analytics:          { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500" },
  Reports:            { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" },
  "Knowledge Center": { bg: "bg-lime-100",   text: "text-lime-700",   dot: "bg-lime-500" },
  "Workflow Builder":  { bg: "bg-pink-100",   text: "text-pink-700",   dot: "bg-pink-500" },
  Delegations:        { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  MCM:                { bg: "bg-emerald-100",text: "text-emerald-700",dot: "bg-emerald-500" },
  Profile:            { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
};

function getModuleColor(module: string) {
  return MODULE_COLORS[module] ?? { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" };
}

// ─── Action meta ─────────────────────────────────────────────────────────────

const ACTION_META: Record<string, { label: string; icon: React.ReactNode; color: string; category: string }> = {
  login:                 { label: "Signed In",             icon: <LogIn className="h-4 w-4" />,        color: "text-green-600 bg-green-50 border-green-200",    category: "auth" },
  logout:                { label: "Signed Out",            icon: <LogOutIcon className="h-4 w-4" />,   color: "text-slate-500 bg-slate-100 border-slate-200",   category: "auth" },
  page_visit:            { label: "Page Visited",          icon: <Eye className="h-4 w-4" />,          color: "text-sky-600 bg-sky-50 border-sky-200",          category: "navigation" },
  profile_update:        { label: "Profile Updated",       icon: <User className="h-4 w-4" />,         color: "text-blue-600 bg-blue-50 border-blue-200",       category: "account" },
  password_change:       { label: "Password Changed",      icon: <Key className="h-4 w-4" />,          color: "text-orange-600 bg-orange-50 border-orange-200", category: "account" },
  request_create:        { label: "Request Created",       icon: <FileText className="h-4 w-4" />,     color: "text-purple-600 bg-purple-50 border-purple-200", category: "requests" },
  request_update:        { label: "Request Updated",       icon: <FileText className="h-4 w-4" />,     color: "text-blue-600 bg-blue-50 border-blue-200",       category: "requests" },
  request_submit:        { label: "Request Submitted",     icon: <FileText className="h-4 w-4" />,     color: "text-purple-600 bg-purple-50 border-purple-200", category: "requests" },
  request_cancel:        { label: "Request Cancelled",     icon: <Ban className="h-4 w-4" />,          color: "text-red-500 bg-red-50 border-red-200",          category: "requests" },
  request_approve:       { label: "Request Approved",      icon: <CheckCircle2 className="h-4 w-4" />, color: "text-green-600 bg-green-50 border-green-200",    category: "approvals" },
  request_reject:        { label: "Request Rejected",      icon: <XCircle className="h-4 w-4" />,      color: "text-red-600 bg-red-50 border-red-200",          category: "approvals" },
  request_clarification: { label: "Clarification Sent",    icon: <FileText className="h-4 w-4" />,     color: "text-yellow-600 bg-yellow-50 border-yellow-200", category: "approvals" },
  request_resubmit:      { label: "Request Resubmitted",   icon: <FileText className="h-4 w-4" />,     color: "text-purple-600 bg-purple-50 border-purple-200", category: "requests" },
  user_create:           { label: "User Created",          icon: <User className="h-4 w-4" />,         color: "text-green-600 bg-green-50 border-green-200",    category: "admin" },
  user_update:           { label: "User Updated",          icon: <User className="h-4 w-4" />,         color: "text-blue-600 bg-blue-50 border-blue-200",       category: "admin" },
  user_delete:           { label: "User Deleted",          icon: <Trash2 className="h-4 w-4" />,       color: "text-red-600 bg-red-50 border-red-200",          category: "admin" },
  user_activate:         { label: "User Activated",        icon: <User className="h-4 w-4" />,         color: "text-green-600 bg-green-50 border-green-200",    category: "admin" },
  user_deactivate:       { label: "User Deactivated",      icon: <User className="h-4 w-4" />,         color: "text-red-500 bg-red-50 border-red-200",          category: "admin" },
  user_role_assign:      { label: "Role Assigned",         icon: <Shield className="h-4 w-4" />,       color: "text-purple-600 bg-purple-50 border-purple-200", category: "admin" },
  workflow_create:       { label: "Workflow Created",      icon: <Layers className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "admin" },
  workflow_update:       { label: "Workflow Updated",      icon: <Layers className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "admin" },
  workflow_delete:       { label: "Workflow Deleted",      icon: <Layers className="h-4 w-4" />,       color: "text-red-600 bg-red-50 border-red-200",          category: "admin" },
  site_create:           { label: "Site Created",          icon: <MapPin className="h-4 w-4" />,       color: "text-green-600 bg-green-50 border-green-200",    category: "facilities" },
  site_update:           { label: "Site Updated",          icon: <MapPin className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "facilities" },
  site_delete:           { label: "Site Deleted",          icon: <MapPin className="h-4 w-4" />,       color: "text-red-600 bg-red-50 border-red-200",          category: "facilities" },
  zone_create:           { label: "Zone Created",          icon: <MapPin className="h-4 w-4" />,       color: "text-green-600 bg-green-50 border-green-200",    category: "facilities" },
  zone_update:           { label: "Zone Updated",          icon: <MapPin className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "facilities" },
  zone_delete:           { label: "Zone Deleted",          icon: <MapPin className="h-4 w-4" />,       color: "text-red-600 bg-red-50 border-red-200",          category: "facilities" },
  area_create:           { label: "Area Created",          icon: <MapPin className="h-4 w-4" />,       color: "text-green-600 bg-green-50 border-green-200",    category: "facilities" },
  area_update:           { label: "Area Updated",          icon: <MapPin className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "facilities" },
  area_delete:           { label: "Area Deleted",          icon: <MapPin className="h-4 w-4" />,       color: "text-red-600 bg-red-50 border-red-200",          category: "facilities" },
  settings_update:       { label: "Settings Updated",      icon: <Settings className="h-4 w-4" />,     color: "text-blue-600 bg-blue-50 border-blue-200",       category: "admin" },
  file_upload:           { label: "File Uploaded",         icon: <Upload className="h-4 w-4" />,       color: "text-blue-600 bg-blue-50 border-blue-200",       category: "account" },
  checkpoint_action:     { label: "Checkpoint Action",     icon: <Shield className="h-4 w-4" />,       color: "text-purple-600 bg-purple-50 border-purple-200", category: "security" },
};

function getActionMeta(action: string) {
  return ACTION_META[action] ?? {
    label: action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    icon: <Activity className="h-4 w-4" />,
    color: "text-slate-600 bg-slate-100 border-slate-200",
    category: "other",
  };
}

function formatRelativeTime(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatFullDate(date: Date | string): string {
  return new Date(date).toLocaleString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ─── Detail Row ───────────────────────────────────────────────────────────────

function DetailRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-[#F5F0FC] last:border-0">
      <span className="flex-shrink-0 w-4 mt-0.5 text-[#9B9B9B]">{icon}</span>
      <span className="text-xs text-[#6B6B6B] w-28 flex-shrink-0 font-medium">{label}</span>
      <span className="text-xs text-[#1A1A1A] flex-1 break-words">{value}</span>
    </div>
  );
}

// ─── Activity Entry ───────────────────────────────────────────────────────────

function ActivityEntry({ log }: { log: any }) {
  const [expanded, setExpanded] = useState(false);
  const meta = getActionMeta(log.action);
  const details = log.details as Record<string, unknown> | null;
  const isPageVisit = log.action === "page_visit";
  const module = isPageVisit ? String(details?.module ?? "") : "";
  const pageLabel = isPageVisit ? String(details?.label ?? "") : "";
  const pagePath = isPageVisit ? String(details?.path ?? "") : "";
  const modColor = getModuleColor(module);

  const summary = (() => {
    if (isPageVisit) return pageLabel || pagePath;
    if (details?.requestNumber) return `Request ${details.requestNumber}`;
    if (details?.comments) return String(details.comments).slice(0, 100);
    if (details?.type) return `Type: ${details.type}`;
    if (details?.label) return String(details.label);
    if (log.entityType && log.entityType !== "session" && log.entityType !== "page") {
      return `${log.entityType.replace(/_/g, " ")}${log.entityId ? ` #${log.entityId}` : ""}`;
    }
    return null;
  })();

  return (
    <div
      className={`border rounded-xl mb-2 overflow-hidden transition-all duration-200 ${
        expanded
          ? "border-[#C4B5E0] shadow-sm bg-white"
          : "border-transparent bg-white hover:border-[#E0D8F0] hover:shadow-sm"
      }`}
      style={{ boxShadow: expanded ? "0 1px 6px rgba(91,44,147,0.08)" : undefined }}
    >
      <button
        className="w-full text-left px-4 py-3 flex items-center gap-3"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Action icon bubble */}
        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border ${meta.color}`}>
          {meta.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[#1A1A1A]">{meta.label}</span>
            {isPageVisit && module && (
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${modColor.bg} ${modColor.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${modColor.dot}`} />
                {module}
              </span>
            )}
            {!isPageVisit && log.entityType && log.entityType !== "session" && (
              <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F0EBF8] text-[#5B2C93]">
                {log.entityType.replace(/_/g, " ")}
                {log.entityId ? ` #${log.entityId}` : ""}
              </span>
            )}
          </div>
          {summary && (
            <p className="text-xs text-[#6B6B6B] mt-0.5 truncate">{summary}</p>
          )}
        </div>

        {/* Timestamp + chevron */}
        <div className="flex-shrink-0 flex items-center gap-2 ml-2">
          <div className="text-right">
            <p className="text-xs font-medium text-[#6B6B6B]">{formatRelativeTime(log.createdAt)}</p>
            <p className="text-[10px] text-[#B0B0B0]">
              {new Date(log.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <span className="text-[#C4B5E0]">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </div>
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="border-t border-[#F0EBF8] bg-[#FDFAFF] px-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            <div>
              <p className="text-[10px] font-bold text-[#9B9B9B] uppercase tracking-widest mb-2">Event Details</p>
              <DetailRow label="Action" value={meta.label} icon={<Activity className="h-3.5 w-3.5" />} />
              <DetailRow label="Category" value={<span className="capitalize">{meta.category}</span>} icon={<Layers className="h-3.5 w-3.5" />} />
              {isPageVisit && (
                <>
                  <DetailRow label="Module" value={module} icon={<Monitor className="h-3.5 w-3.5" />} />
                  <DetailRow label="Page" value={pageLabel} icon={<Eye className="h-3.5 w-3.5" />} />
                  <DetailRow
                    label="Path"
                    value={<code className="font-mono text-[11px] bg-[#F0EBF8] px-1.5 py-0.5 rounded">{pagePath}</code>}
                    icon={<ArrowRight className="h-3.5 w-3.5" />}
                  />
                </>
              )}
              {!isPageVisit && log.entityType && log.entityType !== "session" && (
                <DetailRow
                  label="Entity"
                  value={`${log.entityType.replace(/_/g, " ")}${log.entityId ? ` #${log.entityId}` : ""}`}
                  icon={<Info className="h-3.5 w-3.5" />}
                />
              )}
              {details?.requestNumber && (
                <DetailRow label="Request #" value={String(details.requestNumber)} icon={<FileText className="h-3.5 w-3.5" />} />
              )}
              {details?.type && (
                <DetailRow label="Type" value={String(details.type)} icon={<Layers className="h-3.5 w-3.5" />} />
              )}
              {details?.comments && (
                <DetailRow label="Comments" value={String(details.comments)} icon={<FileText className="h-3.5 w-3.5" />} />
              )}
              {details?.status && (
                <DetailRow label="Status" value={String(details.status)} icon={<CheckCircle2 className="h-3.5 w-3.5" />} />
              )}
              {details?.site && (
                <DetailRow label="Site" value={String(details.site)} icon={<MapPin className="h-3.5 w-3.5" />} />
              )}
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#9B9B9B] uppercase tracking-widest mb-2">Session Info</p>
              <DetailRow label="Timestamp" value={formatFullDate(log.createdAt)} icon={<Clock className="h-3.5 w-3.5" />} />
              {log.ipAddress && (
                <DetailRow label="IP Address" value={log.ipAddress} icon={<Globe className="h-3.5 w-3.5" />} />
              )}
              {log.userAgent && (
                <DetailRow
                  label="User Agent"
                  value={
                    <span className="truncate block max-w-[200px]" title={log.userAgent}>
                      {log.userAgent.length > 60 ? log.userAgent.slice(0, 60) + "…" : log.userAgent}
                    </span>
                  }
                  icon={<Monitor className="h-3.5 w-3.5" />}
                />
              )}
              <DetailRow
                label="Event ID"
                value={<code className="font-mono text-[11px] bg-[#F0EBF8] px-1.5 py-0.5 rounded">#{log.id}</code>}
                icon={<Shield className="h-3.5 w-3.5" />}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Activity Log Tab ─────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: "all",        label: "All Activity" },
  { value: "navigation", label: "Navigation" },
  { value: "auth",       label: "Authentication" },
  { value: "requests",   label: "Requests" },
  { value: "approvals",  label: "Approvals" },
  { value: "account",    label: "Account" },
  { value: "admin",      label: "Administration" },
  { value: "facilities", label: "Facilities" },
  { value: "security",   label: "Security" },
];

function ActivityLogTab() {
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const LIMIT = 50;

  const { data, isLoading, error, refetch, isFetching } = trpc.users.getMyActivityLog.useQuery(
    { limit: LIMIT, offset },
    { staleTime: 0 }
  );

  const allLogs = data?.logs ?? [];

  const logs = allLogs.filter((log: any) => {
    const meta = getActionMeta(log.action);
    const details = log.details as Record<string, unknown> | null;
    const matchCategory = categoryFilter === "all" || meta.category === categoryFilter;
    if (!matchCategory) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      meta.label.toLowerCase().includes(q) ||
      String(details?.module ?? "").toLowerCase().includes(q) ||
      String(details?.label ?? "").toLowerCase().includes(q) ||
      String(details?.requestNumber ?? "").toLowerCase().includes(q) ||
      String(log.entityType ?? "").toLowerCase().includes(q)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[#5B2C93]" />
        <span className="ml-2 text-[#6B6B6B] text-sm">Loading activity…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-red-500 gap-2">
        <AlertCircle className="h-6 w-6" />
        <span className="text-sm">Failed to load activity log</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9B9B9B]" />
          <Input
            placeholder="Search events, modules, requests…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs border-[#E0D8F0] focus-visible:ring-[#5B2C93] bg-white"
          />
        </div>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setOffset(0); }}>
          <SelectTrigger className="h-8 text-xs w-40 border-[#E0D8F0] focus:ring-[#5B2C93] bg-white">
            <Filter className="h-3 w-3 mr-1 text-[#9B9B9B]" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-[#5B2C93] hover:bg-[#F0EBF8] ml-auto"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Stats strip */}
      <div className="flex items-center gap-3 text-xs text-[#9B9B9B] pb-2 border-b border-[#F0EBF8]">
        <span className="font-semibold text-[#1A1A1A]">{logs.length} events</span>
        {categoryFilter !== "all" && (
          <span>filtered by <span className="text-[#5B2C93] font-medium">{CATEGORY_OPTIONS.find((o) => o.value === categoryFilter)?.label}</span></span>
        )}
        {search && (
          <span>matching "<span className="text-[#5B2C93] font-medium">{search}</span>"</span>
        )}
        {(search || categoryFilter !== "all") && (
          <button
            className="ml-auto text-[#5B2C93] hover:underline text-xs"
            onClick={() => { setSearch(""); setCategoryFilter("all"); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Empty state */}
      {logs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-14 text-[#6B6B6B]">
          <div className="w-14 h-14 rounded-full bg-[#F0EBF8] flex items-center justify-center mb-4">
            <Activity className="h-7 w-7 text-[#C4B5E0]" />
          </div>
          <p className="font-semibold text-sm text-[#2C2C2C]">No matching activity</p>
          <p className="text-xs mt-1 text-[#9B9B9B] text-center max-w-xs">
            {search || categoryFilter !== "all"
              ? "Try adjusting your filters."
              : "Sign-ins, page visits, requests, and approvals will appear here."}
          </p>
          {(search || categoryFilter !== "all") && (
            <Button variant="outline" size="sm" className="mt-4 text-xs border-[#E0D8F0]" onClick={() => { setSearch(""); setCategoryFilter("all"); }}>
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Timeline */}
      {logs.length > 0 && (
        <div>
          {logs.map((log: any) => (
            <ActivityEntry key={log.id} log={log} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {allLogs.length > 0 && (
        <div className="flex items-center justify-between pt-3 border-t border-[#F0EBF8]">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 border-[#E0D8F0]"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            Previous
          </Button>
          <span className="text-xs text-[#9B9B9B]">Showing {offset + 1}–{offset + allLogs.length}</span>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 border-[#E0D8F0]"
            disabled={allLogs.length < LIMIT}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "Never";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Main Profile Component ───────────────────────────────────────────────────

export default function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [, setLocation] = useLocation();
  const [formData, setFormData] = useState({ firstName: "", lastName: "", phone: "" });

  const { data: user, isLoading, error, refetch } = trpc.users.me.useQuery();

  const updateProfileMutation = trpc.users.updateProfile.useMutation({
    onSuccess: () => {
      toast.success("Profile updated successfully");
      setIsEditing(false);
      refetch();
    },
    onError: (err: any) => toast.error("Failed to update profile", { description: err.message }),
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => { toast.success("Logged out successfully"); setLocation("/login"); },
    onError: (err: any) => toast.error("Logout failed", { description: err.message }),
  });

  const deleteMeMutation = trpc.users.deleteMe.useMutation({
    onSuccess: () => { toast.success("Account deleted"); setLocation("/login"); },
    onError: (err: any) => toast.error("Failed to delete account", { description: err.message }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-12 w-12 text-red-400" />
        <p className="text-[#6B6B6B]">Failed to load profile</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const firstName = user.name?.split(" ")[0] ?? "";
  const lastName = user.name?.split(" ").slice(1).join(" ") ?? "";
  const initials = (firstName[0] ?? "") + (lastName[0] ?? "");
  const confirmWord = "DELETE";

  const handleSaveProfile = () => {
    updateProfileMutation.mutate({
      firstName: formData.firstName || firstName,
      lastName: formData.lastName || lastName,
      phone: formData.phone,
    });
  };

  return (
    // -mx-4 md:-mx-6 cancels the Layout padding so the hero bleeds edge-to-edge
    <div className="-mx-4 md:-mx-6 -mt-4 md:-mt-6">
      {/* ── Hero Banner ── */}
      <div
        className="relative w-full px-6 md:px-10 pt-8 pb-0"
        style={{
          background: "linear-gradient(135deg, #6B3FA0 0%, #8B5CC8 50%, #9D6ED4 100%)",
        }}
      >
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-20 w-40 h-40 rounded-full opacity-5"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 70%)", transform: "translateY(50%)" }} />

        <div className="relative flex flex-col sm:flex-row items-center gap-5 pb-0 mt-2.5">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/40 flex items-center justify-center shadow-xl">
              <span className="text-2xl font-bold text-white">{initials || "?"}</span>
            </div>
            <button
              onClick={() => { setIsEditing(true); setFormData({ firstName, lastName, phone: "" }); }}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-[#F0EBF8] transition-colors"
            >
              <Edit3 className="h-3 w-3 text-[#5B2C93]" />
            </button>
          </div>

          {/* Name + meta */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white leading-tight">{user.name || "Unknown User"}</h1>
            <p className="text-white/70 text-sm mt-0.5">{user.email}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                <Shield className="h-3 w-3" />
                {user.role === "admin" ? "Administrator" : "User"}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-white/60">
                <Calendar className="h-3 w-3" />
                Joined {new Date(user.createdAt ?? "").toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              </span>
            </div>
          </div>
        </div>

        {/* Tab bar — sits on the bottom edge of the banner */}
        <div className="relative mt-4">
          <Tabs defaultValue="activity" className="w-full">
            <TabsList className="bg-transparent border-0 rounded-none h-auto gap-0 p-0 w-full justify-start">
              <TabsTrigger
                value="general"
                className="relative px-5 py-3 text-sm font-medium text-white/60 bg-transparent rounded-none border-0 shadow-none
                  data-[state=active]:text-white data-[state=active]:bg-transparent data-[state=active]:shadow-none
                  after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full
                  data-[state=active]:after:bg-white after:bg-transparent hover:text-white/90"
              >
                General Info
              </TabsTrigger>
              <TabsTrigger
                value="activity"
                className="relative px-5 py-3 text-sm font-medium text-white/60 bg-transparent rounded-none border-0 shadow-none
                  data-[state=active]:text-white data-[state=active]:bg-transparent data-[state=active]:shadow-none
                  after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full
                  data-[state=active]:after:bg-white after:bg-transparent hover:text-white/90"
              >
                Activity Log
              </TabsTrigger>
            </TabsList>

            {/* ── Tab content lives OUTSIDE the banner ── */}
            <div className="bg-white px-6 md:px-10 py-6 -mx-6 md:-mx-10">
              {/* ── General Info ── */}
              <TabsContent value="general" className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {/* Left: Quick actions */}
                  <div className="space-y-4">
                    <Card className="border border-[#F0EBF8] shadow-none bg-white">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-[#6B6B6B] uppercase tracking-wider">Quick Actions</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1.5 pt-0">
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-sm text-[#32363A] hover:bg-[#F0EBF8] hover:text-[#5B2C93] h-9"
                          onClick={() => setLocation("/settings")}
                        >
                          <Key className="h-4 w-4 mr-2 text-[#5B2C93]" /> Change Password
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-sm text-[#32363A] hover:bg-[#F0EBF8] hover:text-[#5B2C93] h-9"
                          onClick={() => setLocation("/settings")}
                        >
                          <Bell className="h-4 w-4 mr-2 text-[#5B2C93]" /> Notification Settings
                        </Button>
                        <div className="border-t border-[#F0EBF8] my-1" />
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-sm text-red-500 hover:text-red-600 hover:bg-red-50 h-9"
                          onClick={() => logoutMutation.mutate()}
                          disabled={logoutMutation.isPending}
                        >
                          {logoutMutation.isPending
                            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            : <LogOut className="h-4 w-4 mr-2" />}
                          Sign Out
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-sm text-red-400 hover:text-red-600 hover:bg-red-50 h-9"
                          onClick={() => setShowDeleteDialog(true)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete Account
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Account meta */}
                    <Card className="border border-[#F0EBF8] shadow-none bg-white">
                      <CardContent className="pt-4 space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#6B6B6B] flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Role</span>
                          <span className="font-medium capitalize text-[#2C2C2C]">{user.role ?? "User"}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#6B6B6B] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Last Sign In</span>
                          <span className="font-medium text-xs text-[#2C2C2C]">{formatDate(user.lastSignedIn)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#6B6B6B] flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Joined</span>
                          <span className="font-medium text-xs text-[#2C2C2C]">{formatDate(user.createdAt)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right: Personal info form */}
                  <div className="lg:col-span-2">
                    <Card className="border border-[#F0EBF8] shadow-none bg-white">
                      <CardHeader className="flex flex-row items-center justify-between pb-4">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-base">
                            <User className="h-4 w-4 text-[#5B2C93]" /> Personal Information
                          </CardTitle>
                          <CardDescription className="mt-1">Manage your name and contact details</CardDescription>
                        </div>
                        {!isEditing && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#E0D8F0] text-[#5B2C93] hover:bg-[#F0EBF8] h-8 text-xs"
                            onClick={() => { setIsEditing(true); setFormData({ firstName, lastName, phone: "" }); }}
                          >
                            <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label htmlFor="firstName" className="text-xs font-medium text-[#6B6B6B]">First Name</Label>
                            <Input
                              id="firstName"
                              value={isEditing ? formData.firstName : firstName}
                              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              disabled={!isEditing}
                              className="border-[#E0D8F0] focus-visible:ring-[#5B2C93] disabled:bg-[#FAFAFA] disabled:text-[#2C2C2C]"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="lastName" className="text-xs font-medium text-[#6B6B6B]">Last Name</Label>
                            <Input
                              id="lastName"
                              value={isEditing ? formData.lastName : lastName}
                              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              disabled={!isEditing}
                              className="border-[#E0D8F0] focus-visible:ring-[#5B2C93] disabled:bg-[#FAFAFA] disabled:text-[#2C2C2C]"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="email" className="text-xs font-medium text-[#6B6B6B]">Email Address</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B9B9B]" />
                            <Input id="email" value={user.email || ""} className="pl-9 bg-[#FAFAFA] border-[#E0D8F0] text-[#6B6B6B]" disabled />
                          </div>
                          <p className="text-[11px] text-[#9B9B9B]">Email cannot be changed. Contact an administrator.</p>
                        </div>

                        {isEditing && (
                          <div className="flex gap-2 pt-2">
                            <Button
                              className="bg-[#5B2C93] hover:bg-[#4A2278] text-white h-9 text-sm"
                              onClick={handleSaveProfile}
                              disabled={updateProfileMutation.isPending}
                            >
                              {updateProfileMutation.isPending
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                                : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
                            </Button>
                            <Button
                              variant="outline"
                              className="h-9 text-sm border-[#E0D8F0]"
                              onClick={() => setIsEditing(false)}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* ── Activity Log ── */}
              <TabsContent value="activity" className="mt-0">
                <Card className="border border-[#F0EBF8] shadow-none bg-white">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Activity className="h-4 w-4 text-[#5B2C93]" /> Activity Log
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Complete audit trail — page visits, sign-ins, requests, approvals. Click any event to expand details.
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ActivityLogTab />
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* ── Delete Account Dialog ── */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Delete Account
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>This action is <strong>permanent and irreversible</strong>. Your account, profile data, and all associated records will be permanently deleted.</p>
              <p className="mt-2">You will be immediately signed out after deletion.</p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="delete-confirm" className="text-sm font-medium">
              Type <span className="font-mono font-bold text-red-600">{confirmWord}</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              placeholder={confirmWord}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="border-red-200 focus-visible:ring-red-400"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteMeMutation.isPending}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (deleteConfirmText === confirmWord) deleteMeMutation.mutate(); }}
              disabled={deleteConfirmText !== confirmWord || deleteMeMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMeMutation.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</>
                : <><Trash2 className="mr-2 h-4 w-4" /> Delete My Account</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
